import React, { useRef, useState } from 'react';
import { useGetIdentity, useNotify } from 'react-admin';
import { Mail, Send, Undo2, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { Funeral } from '../../types';
import { apiFetchResponse } from '../../lib/apiFetch';
import { capitalize, PartnerDocsDialog } from './PartnerDocsDialog';
import { PartnerDocItem, PartnerDocsStatus } from './partnerDocRequirements';
import {
  announcePartnerDocsChanged,
  markPartnerDoc,
  unmarkPartnerDoc,
} from './partnerDocsApi';
import { usePartnerDocsStatus } from './usePartnerDocsStatus';

export { usePartnerDocsStatus };

const fmt = (d: string) =>
  new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export const groupByPartner = (items: PartnerDocItem[]): PartnerDocItem[][] => {
  const groups: PartnerDocItem[][] = [];
  for (const i of items) {
    const g = groups.find((x) => x[0].partnerId === i.partnerId);
    if (g) g.push(i);
    else groups.push([i]);
  }
  return groups;
};

const ROLE_LABEL: Record<string, string> = {
  crematorium: 'crematorium',
  dropoff: 'drop-off location',
  transport: 'transport',
};

/** One chip per document: what state it's in, in words. */
export const PartnerDocChip: React.FC<{ item: PartnerDocItem }> = ({
  item,
}) => {
  const label = capitalize(item.label.en);
  let text: string;
  let tone: string;
  switch (item.state) {
    case 'satisfied':
      text = !item.row
        ? `${label} ✓ on file` // e.g. the MCCD, whoever uploaded it
        : `${label} ✓ ${
            item.row.direction === 'inbound' ? 'received' : 'sent'
          } ${fmt(item.row.satisfied_at)}${
            item.row.method === 'manual' ? ' (marked)' : ''
          }`;
      tone = 'border-green-300 text-green-700';
      break;
    case 'assumed':
      text = `${label}: assumed (${
        item.assumedReason === 'dropoff_released'
          ? 'released to crematorium'
          : 'cremation scheduled'
      })`;
      tone = 'border-border text-muted-foreground';
      break;
    case 'ready_to_send':
      text = `${label} ✗ not sent`;
      tone = 'border-amber-300 text-amber-700';
      break;
    case 'need_document':
      text = `${label} ✗ not on file`;
      tone = 'border-amber-300 text-amber-700';
      break;
    default:
      text = `${label} ⏳ waiting on ${item.partnerName}`;
      tone = 'border-blue-300 text-blue-700';
  }
  return (
    <span
      className={cn(
        'inline-block rounded-full border px-2 py-0.5 text-[11px] leading-4',
        tone
      )}
    >
      {text}
    </span>
  );
};

/** Upload a file into a case document slot through the normal documents endpoint. */
async function uploadCaseDocument(
  funeralId: string | number,
  documentType: string,
  file: File
) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);
  const res = await apiFetchResponse(`documents/funeral/${funeralId}/upload`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({} as { message?: unknown }));
    const msg = typeof err?.message === 'string' ? err.message : null;
    throw new Error(
      msg ||
        `Upload failed (${res.status}). Password-protected PDFs: use the Documents tab.`
    );
  }
}

/**
 * The "Partner documents" step body on the funeral show page (§4): one row per
 * partner, one chip per document, one action per state, plus Mark as sent /
 * received and undo. `compact` = untracked case under Transport Dispatched.
 */
export const PartnerDocsPanel: React.FC<{
  funeral: Funeral;
  compact?: boolean;
}> = ({ funeral, compact }) => {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { status, setStatus } = usePartnerDocsStatus(funeral.id);
  const [dialog, setDialog] = useState<{
    mode: 'send' | 'request';
    items: PartnerDocItem[];
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadType, setUploadType] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  if (!status || status.items.length === 0) return null;
  const groups = groupByPartner(status.items);

  const act = async (fn: () => Promise<PartnerDocsStatus>, ok: string) => {
    setBusy(true);
    try {
      setStatus(await fn());
      announcePartnerDocsChanged(funeral.id);
      notify(ok, { type: 'success' });
    } catch (e) {
      notify(`Failed: ${e instanceof Error ? e.message : e}`, {
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (file: File | undefined) => {
    if (!file || !uploadType) return;
    setBusy(true);
    try {
      await uploadCaseDocument(funeral.id, uploadType, file);
      notify('Document uploaded', { type: 'success' });
      announcePartnerDocsChanged(funeral.id);
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Upload failed', {
        type: 'error',
      });
    } finally {
      setBusy(false);
      setUploadType(null);
    }
  };

  return (
    <div className="mt-1.5 space-y-2">
      {!status.tracked && (
        <p className="text-[11px] text-muted-foreground">
          Partner documents: not tracked (dispatched before{' '}
          {fmt(status.trackedSince)}). You can still send them.
        </p>
      )}
      {groups.map((items) => {
        const p = items[0];
        const sendable = items.filter(
          (i) => i.defaultDirection === 'outbound' && i.onFile
        );
        const unsent = sendable.filter((i) => i.state === 'ready_to_send');
        const waiting = items.filter((i) => i.state === 'awaiting_partner');
        const missing = items.filter((i) => i.state === 'need_document');
        return (
          <div key={p.partnerId} className="space-y-1">
            <div className="text-[12px] font-medium text-foreground">
              {p.partnerName}{' '}
              <span className="font-normal text-muted-foreground">
                ({p.roles.map((r) => ROLE_LABEL[r]).join(' / ')})
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {items.map((i) => (
                <PartnerDocChip key={i.documentType} item={i} />
              ))}
            </div>
            {!compact || sendable.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1">
                {sendable.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[11px]"
                    disabled={busy}
                    onClick={() => setDialog({ mode: 'send', items })}
                  >
                    <Send className="mr-1 h-3 w-3" />
                    {unsent.length > 0 ? 'Send missing' : 'Send'}
                  </Button>
                )}
                {waiting.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[11px]"
                    disabled={busy}
                    onClick={() => setDialog({ mode: 'request', items })}
                  >
                    <Mail className="mr-1 h-3 w-3" /> Request
                  </Button>
                )}
                {[...missing, ...waiting].map((i) => (
                  <Button
                    key={`up-${i.documentType}`}
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[11px]"
                    disabled={busy}
                    onClick={() => {
                      setUploadType(i.documentType);
                      fileRef.current?.click();
                    }}
                  >
                    <Upload className="mr-1 h-3 w-3" /> Upload {i.label.en}…
                  </Button>
                ))}
              </div>
            ) : null}
            {!compact && (
              <div className="flex flex-wrap gap-x-3 text-[11px]">
                {items
                  .filter((i) => i.state !== 'satisfied')
                  .map((i) => {
                    const inbound =
                      i.defaultDirection === 'inbound' &&
                      i.state === 'awaiting_partner';
                    return (
                      <button
                        key={`mark-${i.documentType}`}
                        type="button"
                        disabled={busy}
                        className="text-muted-foreground underline-offset-2 hover:underline"
                        onClick={() =>
                          void act(
                            () =>
                              markPartnerDoc(funeral.id, {
                                partnerId: i.partnerId,
                                documentType: i.documentType,
                                direction: inbound ? 'inbound' : 'outbound',
                                method: 'manual',
                                actorName: identity?.fullName,
                              }),
                            `Marked ${i.label.en} as ${
                              inbound ? 'received' : 'sent'
                            }`
                          )
                        }
                      >
                        Mark {i.label.en} {inbound ? 'received' : 'sent'}
                      </button>
                    );
                  })}
                {items
                  .filter((i) => i.row)
                  .map((i) => (
                    <button
                      key={`undo-${i.documentType}`}
                      type="button"
                      disabled={busy}
                      className="inline-flex items-center gap-0.5 text-muted-foreground underline-offset-2 hover:underline"
                      onClick={() =>
                        void act(
                          () => unmarkPartnerDoc(funeral.id, i.row?.id ?? 0),
                          `Undid ${i.label.en}`
                        )
                      }
                    >
                      <Undo2 className="h-3 w-3" /> Undo {i.label.en}
                    </button>
                  ))}
              </div>
            )}
          </div>
        );
      })}
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          void onFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {dialog && (
        <PartnerDocsDialog
          open
          onOpenChange={(o) => !o && setDialog(null)}
          funeral={funeral}
          mode={dialog.mode}
          items={dialog.items}
        />
      )}
    </div>
  );
};
